<template>
  <div class="md-layout marketplace-page">
    <div class="md-layout-item">
      <md-card class="main-card">
        <!-- Header -->
        <md-card-header class="card-header">
          <div class="header-left">
            <div class="card-icon"><md-icon>public</md-icon></div>
            <div>
              <h4 class="title">Marketplaces</h4>
              <span class="subtitle"
                >{{ tableData.length }} market{{
                  tableData.length !== 1 ? "s" : ""
                }}
                configured</span
              >
            </div>
          </div>
          <md-button class="md-raised add-btn" @click="openCreate">
            <md-icon>add</md-icon> Add Marketplace
          </md-button>
        </md-card-header>

        <!-- Table -->
        <md-card-content class="table-content">
          <div v-if="loading" class="state-box">
            <md-progress-spinner
              md-mode="indeterminate"
              :md-diameter="36"
              :md-stroke="3"
            />
            <p>Loading…</p>
          </div>

          <div v-else-if="tableData.length === 0" class="state-box">
            <md-icon class="empty-icon">travel_explore</md-icon>
            <p>No marketplaces yet</p>
            <small>Add currencies first, then create your marketplaces.</small>
          </div>

          <table v-else class="data-table">
            <thead>
              <tr>
                <th>Market</th>
                <th>Code</th>
                <th>Currency</th>
                <th>Locale</th>
                <th>Timezone</th>
                <th>Default</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in tableData"
                :key="item._id"
                class="data-row"
                :class="{ 'row-default': item.isDefault }"
              >
                <td>
                  <div class="market-cell">
                    <span class="market-flag">{{
                      item.icon || countryFlag(item.code)
                    }}</span>
                    <span class="market-name">{{ item.country }}</span>
                  </div>
                </td>
                <td>
                  <span class="code-badge">{{ item.code }}</span>
                </td>
                <td>
                  <div v-if="item.currencyId" class="currency-cell">
                    <span class="currency-symbol">{{
                      item.currencyId.symbol
                    }}</span>
                    <span class="currency-code">{{
                      item.currencyId.code
                    }}</span>
                  </div>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="text-muted small">{{ item.locale }}</td>
                <td class="text-muted small">{{ item.timezone }}</td>
                <td>
                  <span v-if="item.isDefault" class="default-badge"
                    >Default</span
                  >
                  <button
                    v-else
                    class="set-default-btn"
                    @click="setDefault(item)"
                    title="Set as default"
                  >
                    Set Default
                  </button>
                </td>
                <td>
                  <span
                    class="status-badge"
                    :class="item.isActive ? 'active' : 'inactive'"
                  >
                    {{ item.isActive ? "Active" : "Inactive" }}
                  </span>
                </td>
                <td>
                  <div class="actions-row">
                    <md-button
                      class="md-just-icon md-simple action-btn btn-warning"
                      title="Edit"
                      @click="openEdit(item)"
                    >
                      <md-icon>edit</md-icon>
                    </md-button>
                    <md-button
                      class="md-just-icon md-simple action-btn"
                      :class="item.isActive ? 'btn-muted' : 'btn-success'"
                      :title="item.isActive ? 'Deactivate' : 'Activate'"
                      @click="toggleActive(item)"
                    >
                      <md-icon>{{
                        item.isActive ? "toggle_on" : "toggle_off"
                      }}</md-icon>
                    </md-button>
                    <md-button
                      class="md-just-icon md-simple action-btn btn-danger"
                      title="Delete"
                      @click="deleteMarketplace(item)"
                      :disabled="item.isDefault"
                    >
                      <md-icon>delete_outline</md-icon>
                    </md-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </md-card-content>
      </md-card>
    </div>

    <!-- Create / Edit Modal -->
    <transition name="modal-fade">
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal-box">
          <div class="modal-header">
            <h5>{{ editMode ? "Edit Marketplace" : "Add Marketplace" }}</h5>
            <button class="modal-close" @click="closeModal">
              <md-icon>close</md-icon>
            </button>
          </div>

          <div class="modal-body">
            <div class="form-row two-col">
              <div class="form-group">
                <label class="field-label"
                  >Country Name <span class="req">*</span></label
                >
                <input
                  v-model="form.country"
                  class="field-input"
                  placeholder="United Kingdom"
                />
              </div>
              <div class="form-group">
                <label class="field-label"
                  >Country Code <span class="req">*</span></label
                >
                <input
                  v-model="form.code"
                  class="field-input"
                  placeholder="GB"
                  maxlength="3"
                  :disabled="editMode"
                />
                <small class="hint">ISO 3166-1 alpha-2 (e.g. GB, DE, FR)</small>
              </div>
            </div>

            <div class="form-row two-col">
              <div class="form-group">
                <label class="field-label"
                  >Currency <span class="req">*</span></label
                >
                <select
                  v-model="form.currencyId"
                  class="field-input field-select"
                >
                  <option value="">— select currency —</option>
                  <option v-for="c in currencies" :key="c._id" :value="c._id">
                    {{ c.code }} — {{ c.name }} ({{ c.symbol }})
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label class="field-label">Flag Emoji</label>
                <input
                  v-model="form.icon"
                  class="field-input"
                  placeholder="🇬🇧"
                  maxlength="4"
                />
                <small class="hint">Optional — paste a flag emoji</small>
              </div>
            </div>

            <div class="form-row two-col">
              <div class="form-group">
                <label class="field-label">Locale</label>
                <input
                  v-model="form.locale"
                  class="field-input"
                  placeholder="en-GB"
                />
                <small class="hint"
                  >BCP 47 locale (e.g. en-GB, de-DE, fr-FR)</small
                >
              </div>
              <div class="form-group">
                <label class="field-label">Timezone</label>
                <input
                  v-model="form.timezone"
                  class="field-input"
                  placeholder="Europe/London"
                />
              </div>
            </div>

            <div class="form-row two-col">
              <div class="form-group toggle-group">
                <label class="field-label">Set as Default</label>
                <div class="toggle-row">
                  <md-switch v-model="form.isDefault" />
                  <span class="toggle-text">{{
                    form.isDefault ? "Yes — default marketplace" : "No"
                  }}</span>
                </div>
              </div>
              <div class="form-group toggle-group">
                <label class="field-label">Active</label>
                <div class="toggle-row">
                  <md-switch v-model="form.isActive" />
                  <span class="toggle-text">{{
                    form.isActive ? "Active" : "Inactive"
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn-cancel" @click="closeModal">Cancel</button>
            <button class="btn-save" @click="saveForm" :disabled="saving">
              <i v-if="saving" class="fa fa-spinner fa-spin"></i>
              <md-icon v-else>check</md-icon>
              {{ editMode ? "Save Changes" : "Create Marketplace" }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ProductService from "../../services/SuperAdmin/Kids/ProductService";
import Swal from "sweetalert2";

export default {
  data() {
    return {
      loading: false,
      saving: false,
      showModal: false,
      editMode: false,
      tableData: [],
      currencies: [],
      form: this.defaultForm(),
    };
  },
  methods: {
    defaultForm() {
      return {
        country: "",
        code: "",
        currencyId: "",
        icon: "",
        locale: "en-GB",
        timezone: "Europe/London",
        isDefault: false,
        isActive: true,
      };
    },
    countryFlag(code) {
      // Convert country code to flag emoji
      try {
        return code
          .toUpperCase()
          .replace(/./g, (c) => String.fromCodePoint(c.charCodeAt(0) + 127397));
      } catch (_) {
        return "🌍";
      }
    },
    openCreate() {
      this.form = this.defaultForm();
      this.editMode = false;
      this.showModal = true;
    },
    openEdit(item) {
      this.form = {
        _id: item._id,
        country: item.country,
        code: item.code,
        currencyId: item.currencyId?._id || item.currencyId,
        icon: item.icon || "",
        locale: item.locale,
        timezone: item.timezone,
        isDefault: item.isDefault,
        isActive: item.isActive,
      };
      this.editMode = true;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async saveForm() {
      if (!this.form.country || !this.form.code || !this.form.currencyId) {
        this.$toast.error("Country, Code and Currency are required.");
        return;
      }
      this.saving = true;
      try {
        if (this.editMode) {
          await ProductService.updateMarketplace(this.form);
          this.$toast.success("Marketplace updated.");
        } else {
          await ProductService.createMarketplace(this.form);
          this.$toast.success("Marketplace created.");
        }
        this.closeModal();
        this.loadData();
      } catch (e) {
        this.$toast.error(
          e.response?.data?.respones_msg || "Operation failed."
        );
      } finally {
        this.saving = false;
      }
    },
    async setDefault(item) {
      try {
        await ProductService.setDefaultMarketplace({ _id: item._id });
        this.$toast.success(`${item.country} set as default marketplace.`);
        this.loadData();
      } catch (e) {
        this.$toast.error(e.response?.data?.respones_msg || "Failed.");
      }
    },
    async toggleActive(item) {
      try {
        await ProductService.toggleMarketplaceActive({
          _id: item._id,
          isActive: !item.isActive,
        });
        this.$toast.success(
          `Marketplace ${item.isActive ? "deactivated" : "activated"}.`
        );
        this.loadData();
      } catch (e) {
        this.$toast.error(e.response?.data?.respones_msg || "Failed.");
      }
    },
    deleteMarketplace(item) {
      Swal.fire({
        title: "Delete marketplace?",
        text: `${item.country} (${item.code}) will be removed.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#f44336",
        confirmButtonText: "Delete",
      }).then(async (result) => {
        if (result.value) {
          try {
            await ProductService.deleteMarketplace({ id: item._id });
            this.$toast.success("Marketplace deleted.");
            this.loadData();
          } catch (e) {
            this.$toast.error(
              e.response?.data?.respones_msg || "Delete failed."
            );
          }
        }
      });
    },
    loadData() {
      this.loading = true;
      Promise.all([
        ProductService.marketplaceList(),
        ProductService.currencyList(),
      ])
        .then(([mpRes, curRes]) => {
          this.tableData = mpRes.data.data;
          this.currencies = curRes.data.data.filter((c) => c.isActive);
        })
        .catch((e) => {
          this.$toast.error(
            e.response?.data?.respones_msg || "Failed to load."
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  beforeMount() {
    this.loadData();
  },
};
</script>

<style scoped>
.marketplace-page {
  font-family: "Segoe UI", sans-serif;
}
.main-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  background: linear-gradient(135deg, #6a1b9a 0%, #38006b 100%);
  color: white;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.card-icon {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.card-icon .md-icon {
  color: white !important;
}
.title {
  color: white;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}
.subtitle {
  color: rgba(255, 255, 255, 0.75);
  font-size: 13px;
}
.add-btn {
  background: rgba(255, 255, 255, 0.18) !important;
  color: white !important;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.table-content {
  padding: 0;
}
.data-table {
  width: 100%;
  border-collapse: collapse;
}
.data-table thead tr {
  background: #f5f5f5;
}
.data-table th {
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
  border-bottom: 2px solid #e0e0e0;
}
.data-table td {
  padding: 12px 16px;
  font-size: 14px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}
.data-row:hover td {
  background: #fdf8ff;
}
.row-default td {
  background: #f9f0ff !important;
}
.text-muted {
  color: #888;
}
.small {
  font-size: 12px;
}

.market-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
.market-flag {
  font-size: 22px;
}
.market-name {
  font-weight: 600;
  color: #333;
}
.code-badge {
  background: #ede7f6;
  color: #4a148c;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  font-family: monospace;
  letter-spacing: 1px;
}
.currency-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}
.currency-symbol {
  font-size: 18px;
  font-weight: 700;
  color: #333;
}
.currency-code {
  font-size: 12px;
  color: #888;
}
.default-badge {
  background: #6a1b9a;
  color: white;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}
.set-default-btn {
  background: none;
  border: 1px dashed #9c27b0;
  color: #9c27b0;
  border-radius: 8px;
  padding: 3px 10px;
  font-size: 11px;
  cursor: pointer;
  font-weight: 600;
}
.set-default-btn:hover {
  background: #f3e5f5;
}
.status-badge {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}
.status-badge.active {
  background: #e8f5e9;
  color: #2e7d32;
}
.status-badge.inactive {
  background: #fce4ec;
  color: #c62828;
}

.actions-row {
  display: flex;
  gap: 2px;
}
.action-btn.md-just-icon {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 8px !important;
}
.action-btn.md-just-icon .md-icon {
  font-size: 17px !important;
}
.action-btn.btn-warning .md-icon {
  color: #f57c00 !important;
}
.action-btn.btn-danger .md-icon {
  color: #c62828 !important;
}
.action-btn.btn-success .md-icon {
  color: #2e7d32 !important;
}
.action-btn.btn-muted .md-icon {
  color: #888 !important;
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #aaa;
  text-align: center;
}
.empty-icon {
  font-size: 48px !important;
  color: #ddd !important;
  margin-bottom: 12px;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.modal-box {
  background: white;
  border-radius: 14px;
  width: 100%;
  max-width: 560px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  max-height: 90vh;
  overflow-y: auto;
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(135deg, #6a1b9a, #38006b);
  color: white;
  position: sticky;
  top: 0;
  z-index: 1;
}
.modal-header h5 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: white;
}
.modal-close {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.modal-close .md-icon {
  color: white !important;
  font-size: 16px !important;
}
.modal-body {
  padding: 24px;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  position: sticky;
  bottom: 0;
  background: white;
}

.form-row {
  display: flex;
  gap: 16px;
}
.form-row.two-col > * {
  flex: 1;
}
.form-group {
  margin-bottom: 16px;
}
.field-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #555;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.req {
  color: #e53935;
}
.hint {
  font-size: 11px;
  color: #aaa;
  display: block;
  margin-top: 3px;
}
.field-input {
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  background: white;
}
.field-input:focus {
  border-color: #6a1b9a;
}
.field-input:disabled {
  background: #f5f5f5;
  color: #888;
}
.field-select {
  -webkit-appearance: none;
  cursor: pointer;
}
.toggle-group {
  display: flex;
  flex-direction: column;
}
.toggle-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.toggle-text {
  font-size: 13px;
  color: #555;
}
.btn-cancel {
  padding: 9px 20px;
  border-radius: 8px;
  border: 1.5px solid #e0e0e0;
  background: white;
  color: #666;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.btn-save {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  border-radius: 8px;
  border: none;
  background: #6a1b9a;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.btn-save:hover:not(:disabled) {
  background: #4a148c;
}
.btn-save:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
.btn-save .md-icon {
  color: white !important;
  font-size: 17px !important;
}
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s;
}
.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
